import type { ReactNode } from "react";
import {
  COST_RANGE_CAPTION,
  cn,
  costRange,
  fmtUsd,
  useDisclosure,
  type CostRange,
} from "@dearbill/shared";
import type { CostToolHistoryEntry } from "./useCostToolMemory";

export interface HistoryPanelProps {
  entries: CostToolHistoryEntry[];
  /** The current plan year; a card names its year only when the check ran under a later one. */
  currentYear: number;
  /** Restore a check into the main flow (the plan and drug rows; the estimate re-runs). */
  onRestore: (entry: CostToolHistoryEntry) => void;
  /** Remove one entry (the card's X). */
  onRemove: (id: string) => void;
  /** One-tap clear of the history only, never the plan and drug recents. */
  onClear: () => void;
  /** Layout hook for the caller's responsive placement. */
  className?: string;
}

/**
 * Each verdict's status-dot class and sr-only announcement. The sr-only text
 * doesn't repeat the verdict banner's headline, because duplicate strings would
 * trip exact-match queries and announce twice.
 */
const VERDICT_DOT: Record<CostToolHistoryEntry["verdict"], { className: string; label: string }> = {
  helps: { className: "bg-success", label: "Verdict: helps" },
  no_help: { className: "bg-slate-teal", label: "Verdict: no help" },
  unknown: { className: "bg-warning", label: "Verdict: unknown" },
};

/** Coarse relative time ("just now", "5m ago", "2h ago", "3d ago") without a date library. */
function relativeTime(at: number, now: number): string {
  const mins = Math.floor(Math.max(0, now - at) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** Whole-dollar preview figure, because the counter conversation rounds to the dollar. */
const wholeUsd = (dollars: number): string => fmtUsd(Math.round(dollars));

/**
 * "est. $103/mo with M3P", a range ("est. $31–$56/mo with M3P") when the stored
 * deductible-met pass is lower, or "Price not available" when the check
 * couldn't be priced. An entry with no stored low end reads the single figure.
 */
function monthlyPreview(entry: CostToolHistoryEntry): CostRange {
  if (entry.monthlyWithM3p === null) return { text: "Price not available", ranged: false };
  const range = costRange(entry.monthlyWithM3pMin, entry.monthlyWithM3p, wholeUsd);
  return { text: `est. ${range.text}/mo with M3P`, ranged: range.ranged };
}

/**
 * "Your last checks": this terminal's last few completed checks (inputs and
 * preview numbers), so a returning member's question ("what was that Eliquis
 * number on the Humana plan?") is one tap, not a re-search.
 *
 * For privacy the list lives only in client-side localStorage, is unnamed,
 * holds nothing patient-shaped, expires after `useCostToolMemory`'s TTL and
 * clears in one tap; the caption under the header says so.
 *
 * Tapping a card restores the inputs, and the estimate re-runs against current
 * data, because the stored numbers are only a preview. The panel renders
 * nothing when empty. One instance serves both breakpoints, because duplicate
 * cards per breakpoint would double every button in the accessibility tree. On
 * lg it shows a plain header and an always-visible list, which the caller
 * places in the right rail. Below lg the same list sits behind a "Your last
 * checks (N)" disclosure, so the counter flow stays first on mobile.
 */
export function HistoryPanel({
  entries,
  currentYear,
  onRestore,
  onRemove,
  onClear,
  className,
}: HistoryPanelProps): ReactNode {
  const disclosure = useDisclosure();

  if (entries.length === 0) return null;

  return (
    <section
      aria-labelledby="cost-tool-history-heading"
      className={cn("border-border-strong rounded-2xl border bg-white p-[22px]", className)}
    >
      <h3
        id="cost-tool-history-heading"
        className="text-foreground hidden font-serif text-[19px] font-semibold leading-[26px] lg:block"
      >
        Your last checks
      </h3>

      <button
        type="button"
        {...disclosure.triggerProps}
        className="text-foreground cursor-pointer font-serif text-[19px] font-semibold leading-[26px] underline underline-offset-2 lg:hidden"
      >
        Your last checks ({entries.length})
      </button>

      <div {...disclosure.panelProps} className={cn(!disclosure.open && "hidden lg:block")}>
        <p className="text-slate-teal mt-2 text-sm leading-5">
          Kept on this computer only, for 30 days. Not attached to any patient.
        </p>
        {entries.some((entry) => monthlyPreview(entry).ranged) && (
          <p className="text-slate-teal mt-1 text-sm leading-5">{COST_RANGE_CAPTION}</p>
        )}
        <ul className="mt-3.5 space-y-2">
          {entries.map((entry) => (
            <HistoryCard
              key={entry.id}
              entry={entry}
              currentYear={currentYear}
              onRestore={() => onRestore(entry)}
              onRemove={() => onRemove(entry.id)}
            />
          ))}
        </ul>
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear this list of checks"
          className="bg-soft text-midnight hover:bg-soft-hover mt-3.5 inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-lg px-4 py-2 text-[13px] font-semibold leading-[1.5] transition"
        >
          Clear this list
        </button>
      </div>
    </section>
  );
}

/**
 * One compressed check card: the plan name with its medication count (the
 * design's "plan · N medications" line, node 2340:222) and verdict dot, the
 * drug names, and the monthly preview with its relative time. A check that ran
 * under a later plan year than `currentYear` names it as "<year> (est.)" on
 * that line and in the restore label, so a this-year and a next-year card for
 * the same inputs tell apart. The whole card is the restore button, a large
 * touch target, and the remove button sits over its corner.
 */
function HistoryCard({
  entry,
  currentYear,
  onRestore,
  onRemove,
}: {
  entry: CostToolHistoryEntry;
  currentYear: number;
  onRestore: () => void;
  onRemove: () => void;
}): ReactNode {
  const dot = VERDICT_DOT[entry.verdict];
  const drugNames = entry.drugs.map((d) => d.drugName).join(", ");
  const count = entry.drugs.length;
  const yearLabel =
    entry.planYear !== undefined && entry.planYear > currentYear
      ? `${entry.planYear} (est.)`
      : null;
  return (
    <li className="border-haze relative min-h-11 rounded-md border bg-white">
      <button
        type="button"
        onClick={onRestore}
        aria-label={`Restore check: ${entry.plan.planName}${yearLabel === null ? "" : ` ${yearLabel}`} — ${drugNames}`}
        className="hover:bg-ice block w-full min-w-0 cursor-pointer rounded-md px-4 py-3 pr-11 text-left"
      >
        <span className="flex items-center gap-2">
          <span className="min-w-0 truncate text-sm font-semibold">
            <span className="text-ember">{entry.plan.planName}</span>
            <span className="text-deep-navy">
              {" "}
              · {count} {count === 1 ? "medication" : "medications"}
              {yearLabel !== null && <> · {yearLabel}</>}
            </span>
          </span>
          <span aria-hidden="true" className={cn("size-2 shrink-0 rounded-full", dot.className)} />
          <span className="sr-only">{dot.label}</span>
        </span>
        <span className="text-slate-teal mt-0.5 block truncate text-xs">{drugNames}</span>
        <span className="text-slate-teal mt-0.5 flex items-baseline justify-between gap-2 text-xs">
          <span className="text-foreground truncate font-medium">{monthlyPreview(entry).text}</span>
          <span className="shrink-0">{relativeTime(entry.at, Date.now())}</span>
        </span>
      </button>
      <button
        type="button"
        aria-label="Remove this check"
        onClick={onRemove}
        className="text-slate-teal hover:bg-ice hover:text-foreground absolute right-2 top-2 inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full"
      >
        <svg
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </li>
  );
}
